/**
 * Dependency-free Core Web Vitals collection.
 *
 * Rather than pull in the `web-vitals` npm package (and a runtime dependency),
 * this reads the same underlying browser performance entries directly through
 * `PerformanceObserver` and the Navigation Timing API, then forwards each
 * metric to the telemetry sink. Covers the platform-supported vitals:
 *
 *   - LCP  (largest-contentful-paint)
 *   - CLS  (layout-shift, summed, excluding recent-input shifts)
 *   - INP  (event timing — worst interaction latency)
 *   - FCP  (paint: first-contentful-paint)
 *   - TTFB (navigation responseStart)
 *
 * All observers are wrapped in try/catch and feature-detected so an
 * unsupported browser silently collects fewer metrics rather than throwing.
 */

import { recordEvent } from './telemetry';

function report(metric: string, value: number, extra?: Record<string, unknown>): void {
  recordEvent('web_vital', 'info', { metric, value: Math.round(value), ...extra });
}

function observe(
  type: string,
  cb: (entries: PerformanceEntryList) => void,
  opts?: PerformanceObserverInit
): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const po = new PerformanceObserver((list) => cb(list.getEntries()));
    po.observe({ type, buffered: true, ...opts } as PerformanceObserverInit);
  } catch {
    /* unsupported entry type — skip this metric. */
  }
}

export function initWebVitals(): void {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return;

  // TTFB — from the navigation entry's responseStart.
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (nav && nav.responseStart > 0) {
      report('TTFB', nav.responseStart);
    }
  } catch {
    /* ignore */
  }

  // FCP — first-contentful-paint paint entry.
  observe('paint', (entries) => {
    for (const entry of entries) {
      if (entry.name === 'first-contentful-paint') report('FCP', entry.startTime);
    }
  });

  // LCP — keep the latest; the final value is the largest before user input.
  let lcpValue = 0;
  observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcpValue = last.startTime;
  });

  // CLS — sum layout shifts that weren't caused by recent user input.
  let clsValue = 0;
  observe('layout-shift', (entries) => {
    for (const entry of entries as unknown as Array<{ value: number; hadRecentInput: boolean }>) {
      if (!entry.hadRecentInput) clsValue += entry.value;
    }
  });

  // INP approximation — track the worst event processing latency.
  let worstInteraction = 0;
  observe(
    'event',
    (entries) => {
      for (const entry of entries as unknown as Array<{ duration: number }>) {
        if (entry.duration > worstInteraction) worstInteraction = entry.duration;
      }
    },
    { durationThreshold: 40 } as PerformanceObserverInit
  );

  // Flush the accumulated session metrics when the page is hidden/unloading —
  // that's the point at which LCP/CLS/INP are final.
  const flushFinal = () => {
    if (lcpValue > 0) report('LCP', lcpValue);
    if (clsValue > 0) report('CLS', clsValue * 1000, { unit: 'milli-cls' });
    if (worstInteraction > 0) report('INP', worstInteraction);
  };
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushFinal();
  });
  window.addEventListener('pagehide', flushFinal);
}
